import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { Workspace, Supply } from '../types'

const STORAGE_KEY = 'fax-sup-session'

function loadFromStorage(): { workspace: Workspace | null; supply: Supply | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { workspace: null, supply: null }
    const parsed = JSON.parse(raw)
    return {
      workspace: parsed.workspace ?? null,
      supply: parsed.supply ?? null,
    }
  } catch {
    return { workspace: null, supply: null }
  }
}

function saveToStorage(workspace: Workspace | null, supply: Supply | null): void {
  if (workspace && supply) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ workspace, supply }))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export const useSessionStore = defineStore('session', () => {
  const stored = loadFromStorage()
  const workspaces = ref<Workspace[]>([])
  const selectedWorkspace = ref<Workspace | null>(stored.workspace)
  const selectedSupply = ref<Supply | null>(stored.supply)

  const workspaceId = computed(() => selectedWorkspace.value?.acc_id ?? null)
  const suppliedAccId = computed(() => selectedSupply.value?.acc_id ?? null)

  watch([selectedWorkspace, selectedSupply], ([ws, sup]) => {
    saveToStorage(ws, sup)
  })

  function setWorkspaces(data: Workspace[]): void {
    workspaces.value = data
  }

  function selectWorkspace(workspace: Workspace): void {
    selectedWorkspace.value = workspace
    selectedSupply.value = null
  }

  function selectSupply(supply: Supply): void {
    selectedSupply.value = supply
  }

  function resetSelection(): void {
    selectedWorkspace.value = null
    selectedSupply.value = null
  }

  function clear(): void {
    workspaces.value = []
    selectedWorkspace.value = null
    selectedSupply.value = null
  }

  return {
    workspaces,
    selectedWorkspace,
    selectedSupply,
    workspaceId,
    suppliedAccId,
    setWorkspaces,
    selectWorkspace,
    selectSupply,
    resetSelection,
    clear,
  }
})
